import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import loadable from '@loadable/component';
import { PushNotificationsDialogs, usePushNotifications } from '@pegasus/push-messages';
import { useTranslationContext } from '@pegasus/translation';
import { useUpcomingPushMessages } from '@pegasus/upcoming-section';
import { useConfig } from '@browsing/config';
import {
    EarlyAccessPhase,
    PlusEducationalDialog,
    PlusMembershipStatus,
    useEarlyAccessPhase,
} from '@browsing/plus';
import { getCookie } from '@browsing/utils';

import { track } from '@client/helpers/tracking';
import { getFirstName } from '@client/redux/modules/customer';
import {
    showHeaderErrorFlashMessage,
    showHeaderSuccessFlashMessage,
} from '@client/redux/modules/flashMessage';
import { getEarlyAccessCampaigns, pickEarlyAccess } from '@client/redux/modules/mylounge';
import { categorySubLinesSelector } from '@client/redux/modules/myloungeMetadata';

import { useZalandoPlus } from '../use-zalando-plus';

const EASection = loadable(
    () => import(/* webpackChunkName: "early-access-section" */ './early-access-section'),
);

const PlusShowstopper = loadable(
    () => import(/* webpackChunkName: "browsing-plus-showstopper" */ '@browsing/plus'),
    {
        resolveComponent: components => components.PlusShowstopper,
    },
);

const handleOnTrack = (trackEvent: string, data: string) =>
    track(trackEvent, { productCampaign: data });

export const EarlyAccessSection = memo(
    ({
        marginLess,
        renderWhenPhases = [],
    }: {
        marginLess?: boolean;
        renderWhenPhases: EarlyAccessPhase[];
    }) => {
        const dispatch = useDispatch();
        const config = useConfig();
        const { translations } = useTranslationContext();

        const { isSignupEnabled, status, type } = useZalandoPlus();

        const firstName = useSelector(getFirstName);
        const { openCampaigns, upcomingCampaigns } = useSelector(getEarlyAccessCampaigns);
        const categoriesByCampaignId = useSelector(categorySubLinesSelector);

        const signupFilteredUpcomingCampaigns = useMemo(() => {
            if (isSignupEnabled) {
                return openCampaigns.length !== 0 ? [] : upcomingCampaigns.filter(pickEarlyAccess);
            }

            return upcomingCampaigns;
        }, [isSignupEnabled, openCampaigns, upcomingCampaigns]);

        const isNotificationEnabled = useMemo(() => {
            const featureEnabled = config.isEnabled('pushNotification');

            return featureEnabled || getCookie('zl_web_push') === 'true';
        }, [config]);

        const handlePushAPIActivationFinished = useCallback(
            (isActivated: boolean, message: string) => {
                const showFlashMessageFn = isActivated
                    ? showHeaderSuccessFlashMessage
                    : showHeaderErrorFlashMessage;

                dispatch(showFlashMessageFn(message));
            },
            [dispatch],
        );

        const { isSupported, registerUser, checkAccess, dialogControls } = usePushNotifications();

        const isWebPushActive = isNotificationEnabled && isSupported;

        const { subscribeToReminder, subscribedCampaigns, registerToReminder, pendingCampaignId } =
            useUpcomingPushMessages(
                upcomingCampaigns,
                config.appdomain,
                config.notificationTagSuffix,
                handlePushAPIActivationFinished,
                checkAccess,
                registerUser,
                true,
            );

        const notificationDialog = isWebPushActive ? (
            <PushNotificationsDialogs<string>
                dialogControls={dialogControls}
                translations={translations}
                firstName={firstName}
                data={pendingCampaignId}
                onRegister={registerToReminder}
                onTrack={handleOnTrack}
            />
        ) : null;

        const earlyAccessPhase = useEarlyAccessPhase(openCampaigns, upcomingCampaigns);

        if (!isSignupEnabled) {
            return (
                <>
                    {notificationDialog}
                    <EASection
                        isWebPushActive={isWebPushActive}
                        openCampaigns={openCampaigns}
                        subscribedCampaigns={subscribedCampaigns}
                        subscribeToReminder={subscribeToReminder}
                        upcomingCampaigns={upcomingCampaigns}
                    />
                </>
            );
        }

        if (
            !!status &&
            status !== PlusMembershipStatus.NotEligible &&
            renderWhenPhases.includes(earlyAccessPhase)
        ) {
            return (
                <>
                    {notificationDialog}
                    <PlusEducationalDialog context="LOUNGE_MYLOUNGE">
                        <PlusShowstopper
                            categoriesByCampaignId={categoriesByCampaignId}
                            earlyAccessPhase={earlyAccessPhase}
                            isWebPushActive={isWebPushActive}
                            marginLess={marginLess}
                            openCampaigns={openCampaigns}
                            plusMembershipStatus={status}
                            plusMembershipType={type}
                            subscribedCampaigns={subscribedCampaigns}
                            subscribeToReminder={subscribeToReminder}
                            upcomingCampaigns={signupFilteredUpcomingCampaigns}
                        />
                    </PlusEducationalDialog>
                </>
            );
        }

        return null;
    },
);
